﻿import {
  DocumentEditingType,
  ExternalEditingType,
} from 'services/api/api-client.types.ts';
import { QueryFactory } from 'services/api';
import { constantSessionId } from 'helpers/sessionProvider.ts';
import {
  CustomCommandExecutedEventArgs,
  RichEdit,
} from 'components/devexpress/RichEdit/dx-richedit.ts';
import i18next from 'i18next';
import { useCallback } from 'react';
import { Links } from 'application/constants/links.ts';
import { useQueryClient } from '@tanstack/react-query';
import { withLoadingPanel } from 'pages/authorized/documents/components/richedit-helper';
import { CustomDropDownInnerProps } from 'pages/authorized/documents/components/richEdit/types';
import { showErrorToast } from 'components/uikit/toast/showToast';

export async function openDocumentExternally(
  documentId: number,
  externalEditingType: ExternalEditingType,
) {
  const link = await QueryFactory.ExternalEditingClient.openNew(
    documentId,
    externalEditingType,
  );

  window.open(link, '_blank');
}

export const useOpenExternallyDropdown = (
  updateDocumentReadOnlyState: () => Promise<void>,
): CustomDropDownInnerProps => {
  const id = 'openExternallyDropdownId';
  const getDocumentId = () => +window.location.pathname.split('/').at(-1)!;
  const queryClient = useQueryClient();

  const unlockDocument = useCallback(async (documentId: number) => {
    const check = await QueryFactory.EditingLockClient.checkLock(documentId);
    if (
      check.editingType == DocumentEditingType.Office365 ||
      check.sessionIdentifier != constantSessionId
    ) {
      return;
    }

    await QueryFactory.EditingLockClient.unlock(documentId);
  }, []);

  const action = useCallback(
    async (richEdit: RichEdit, e: CustomCommandExecutedEventArgs) => {
      try {
        if (
          ![
            ExternalEditingType.SharepointWordBrowser.toString(),
            ExternalEditingType.SharepointWordApp.toString(),
          ].includes(e.commandName)
        ) {
          return;
        }

        await withLoadingPanel(richEdit, async () => {
          switch (e.commandName as ExternalEditingType) {
            case ExternalEditingType.SharepointWordBrowser:
              await unlockDocument(getDocumentId());
              await openDocumentExternally(
                getDocumentId(),
                ExternalEditingType.SharepointWordBrowser,
              );
              await updateDocumentReadOnlyState();
              await queryClient.invalidateQueries({
                queryKey:
                  QueryFactory.ExternalEditingQuery.getEditingDocumentLinkQueryKey(
                    getDocumentId(),
                  ),
              });
              break;
            case ExternalEditingType.SharepointWordApp:
              await unlockDocument(getDocumentId());
              await openDocumentExternally(
                getDocumentId(),
                ExternalEditingType.SharepointWordApp,
              );
              await updateDocumentReadOnlyState();
              await queryClient.invalidateQueries({
                queryKey:
                  QueryFactory.ExternalEditingQuery.getEditingDocumentLinkQueryKey(
                    getDocumentId(),
                  ),
              });
              break;
          }
        });
      } catch (e) {
        showErrorToast(e);
      }
    },
    [],
  );

  return {
    id,
    enabled: !!Links.Authorized.OpenDocument.matchPath(
      window.location.pathname,
    ),
    text: i18next.t('Page.DocumentEditor.ExternalOpening.OpenInButton'),
    items: [
      {
        id: ExternalEditingType.SharepointWordBrowser,
        text: i18next.t('Page.DocumentEditor.ExternalOpening.Office365'),
      },
      {
        id: ExternalEditingType.SharepointWordApp,
        text: i18next.t('Page.DocumentEditor.ExternalOpening.Word'),
      },
    ],
    icon: 'open-in',
    showText: true,
    action,
  };
};
